import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trackLanguageChange } from '../lib/analytics';
import { clarityTrackLanguageChange, clarityTrackPageView } from '../lib/clarity';

export type Language = 'fr' | 'de' | 'en' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: Record<string, any>;
  t: (key: string, params?: Record<string, string | number>) => string;
  isInitialLoading: boolean;
  isPageLoading: boolean;
  setTranslations: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setIsPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadSpecificModule: (moduleKey: string) => Promise<void>;
}

// Contexte par defaut avec fonctions no-op pour eviter les erreurs
const defaultContextValue: LanguageContextType = {
  language: 'fr',
  setLanguage: () => {},
  translations: {},
  t: (key: string) => key,
  isInitialLoading: false,
  isPageLoading: false,
  setTranslations: () => {},
  setIsPageLoading: () => {},
  loadSpecificModule: async () => {}
};

const LanguageContext = createContext<LanguageContextType>(defaultContextValue);


export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return defaultContextValue;
  }
  return context;
};

// Fonction utilitaire pour déterminer le module de traduction basé sur le pathname
const getTranslationModuleKey = (pathname: string): string | null => {
  // Nettoyer le pathname des préfixes de langue
  const cleanPath = pathname.replace(/^\/(fr|de|en|pt)/, '') || '/';
  
  switch (cleanPath) {
    case '/':
      return 'home';
    case '/services':
      return 'services';
    case '/pneus':
      return 'tires';
    case '/faq':
      return 'faq';
    case '/contact':
      return 'contact';
    case '/legal':
      return 'legal';
    case '/privacy':
      return 'privacy';
    // Pages qui n'ont pas de fichier de traduction dédié (utilisent seulement common)
    case '/gallery':
    case '/custom-request':
    case '/admin':
      return null;
    default:
      return null;
  }
};

// Fonction utilitaire pour charger et mettre en cache un module de traduction
const loadAndCacheModule = async (lang: Language, moduleKey: string): Promise<any> => {
  const cacheKey = `translations_v7_${lang}_${moduleKey}`;
  
  // Vérifier le cache localStorage
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn(`Error reading cache for ${cacheKey}:`, error);
  }

  // Charger depuis le fichier
  try {
    const module = await import(`../i18n/${lang}/${moduleKey}.json`);
    const data = module.default;
    
    // Mettre en cache
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (error) {
      console.warn(`Error caching translations for ${cacheKey}:`, error);
    }
    
    return data;
  } catch (error) {
    console.error(`Error loading translations for ${lang}/${moduleKey}:`, error);
    return {};
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialiser la langue depuis localStorage ou par défaut 'fr'
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      return (saved && ['fr', 'de', 'en', 'pt'].includes(saved)) ? saved as Language : 'fr';
    } catch {
      return 'fr';
    }
  });
  
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [loadedModules, setLoadedModules] = useState<Set<string>>(new Set());
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Fonction pour charger un module spécifique à la demande
  const loadSpecificModule = useCallback(async (moduleKey: string) => {
    // Vérifier si le module est déjà chargé
    if (loadedModules.has(moduleKey)) {
      return;
    }

    setIsPageLoading(true);
    
    try {
      const moduleData = await loadAndCacheModule(language, moduleKey);
      
      setTranslations(prev => ({
        ...prev,
        [moduleKey]: moduleData
      }));
      
      setLoadedModules(prev => new Set([...prev, moduleKey]));
    } catch (error) {
      console.error(`Error loading module ${moduleKey}:`, error);
    } finally {
      setIsPageLoading(false);
    }
  }, [language, loadedModules, setIsPageLoading]);

  // Wrapper pour setLanguage qui sauvegarde dans localStorage
  const setLanguage = useCallback((lang: Language) => {
    const previousLanguage = language;
    setLanguageState(lang);
    try {
      localStorage.setItem('selectedLanguage', lang);
      // Track language change
      if (previousLanguage !== lang) {
        trackLanguageChange(previousLanguage, lang);
        clarityTrackLanguageChange(previousLanguage, lang);
      }
    } catch (error) {
      console.warn('Error saving language to localStorage:', error);
    }
    // Reset l'état pour recharger les traductions
    setLoadedModules(new Set());
    setTranslations({});
    setIsInitialLoading(true);
  }, []);

  // Fonction t pour les traductions
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value !== 'string') {
      return key;
    }

    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, param) => {
        return String(params[param]) || match;
      });
    }

    return value;
  }, [translations]);

  // Chargement initial (common + page initiale)
  useEffect(() => {
    const loadInitialTranslations = async () => {
      setIsInitialLoading(true);
      
      try {
        // Toujours charger common et cookies en premier (composants globaux)
        const commonData = await loadAndCacheModule(language, 'common');
        const cookiesData = await loadAndCacheModule(language, 'cookies');

        const newTranslations = { 
          common: commonData,
          cookies: cookiesData
        };
        const newLoadedModules = new Set(['common', 'cookies']);

        // Déterminer la page initiale
        const initialPath = window.location.pathname;
        const initialModuleKey = getTranslationModuleKey(initialPath);
        
        // Charger le module de la page initiale si nécessaire
        if (initialModuleKey) {
          const moduleData = await loadAndCacheModule(language, initialModuleKey);
          newTranslations[initialModuleKey] = moduleData;
          newLoadedModules.add(initialModuleKey);
        }

        setTranslations(newTranslations);
        setLoadedModules(newLoadedModules);
      } catch (error) {
        console.error('Error loading initial translations:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialTranslations();
  }, [language]);

  // Mettre à jour le titre et la méta-description quand les traductions common sont disponibles
  useEffect(() => {
    if (!isInitialLoading && translations.common) {
      document.documentElement.lang = language;
      
      // Update Clarity language tag
      try {
        const Clarity = (window as any).clarity;
        if (Clarity && Clarity.setTag) {
          Clarity.setTag('current_language', language);
        }
      } catch (error) {
        console.warn('Could not update Clarity language tag:', error);
      }
      
      const title = t('common.siteTitle');
      const metaDescription = t('common.metaDescription');
      
      if (title !== 'common.siteTitle') {
        document.title = title;
      }
      
      const descriptionMeta = document.querySelector('meta[name="description"]');
      if (descriptionMeta && metaDescription !== 'common.metaDescription') {
        descriptionMeta.setAttribute('content', metaDescription);
      }
    }
  }, [language, loadedModules, setTranslations]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      translations, 
      t, 
      isInitialLoading, 
      isPageLoading,
      setTranslations,
      setIsPageLoading,
      loadSpecificModule
    }}>
      {isInitialLoading ? (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-white text-lg font-medium">Chargement...</div>
          </div>
        </div>
      ) : (
        children
      )}
    </LanguageContext.Provider>
  );
};

// Hook pour gérer le chargement paresseux des traductions de page
export const usePageTranslations = () => {
  const location = useLocation();
  const { language, translations, setTranslations, isPageLoading, setIsPageLoading } = useLanguage();
  const [loadedModules, setLoadedModules] = useState<Set<string>>(new Set(['common', 'cookies']));

  useEffect(() => {
    const loadPageTranslations = async () => {
      const currentModuleKey = getTranslationModuleKey(location.pathname);
      
      if (!currentModuleKey || loadedModules.has(currentModuleKey)) {
        return;
      }

      setIsPageLoading(true);
      
      try {
        const moduleData = await loadAndCacheModule(language, currentModuleKey);
        
        setTranslations(prev => ({
          ...prev,
          [currentModuleKey]: moduleData
        }));
        
        setLoadedModules(prev => new Set([...prev, currentModuleKey]));
      } catch (error) {
        console.error(`Error loading page translations for ${currentModuleKey}:`, error);
      } finally {
        setIsPageLoading(false);
      }
    };

    loadPageTranslations();
  }, [location.pathname, language, loadedModules, setTranslations, setIsPageLoading]);

  return { isPageLoading };
};